<template>
  <div>
    <OnboardingLayout
      v-if="!currentSection"
      :current-step="displayStep"
      :total-steps="totalSteps"
    >
      <RoleSelection
        v-if="shouldShowStep('role')"
        :model-value="onboardingData.role"
        @next="handleStep1Next"
        @update:model-value="updateRole"
      />
      <div v-if="shouldShowStep('school')" class="school-selection-wrapper">
        <SchoolSelection
          :model-value="onboardingData.school"
          @next="handleStep2Next"
          @back="handleBack"
          @skip="handleStep2Skip"
          @update:model-value="updateSchool"
        />
      </div>
      <SubjectSelection
        v-if="shouldShowStep('subject')"
        :options="subjectOptions"
        :model-value="onboardingData.subjects || []"
        @next="handleStep3Next"
        @back="handleBack"
        @skip="handleStep3Skip"
        @update:model-value="updateSubjects"
      />
      <GradeSelection
        v-if="shouldShowStep('grade')"
        :options="gradeOptions"
        :model-value="onboardingData.grades || []"
        title="What level(s) are you teaching this year?"
        subtitle="Select all that apply"
        @next="handleStep4Next"
        @back="handleBack"
        @skip="handleStep4Skip"
        @update:model-value="updateGrades"
      />
      <DiscoverySelection
        v-if="shouldShowStep('discovery')"
        :model-value="onboardingData.discoverySource"
        @next="handleStep5Next"
        @back="handleBack"
        @skip="handleStep5Skip"
        @update:model-value="updateDiscoverySource"
      />
      <HelpSelection
        v-if="shouldShowStep('help')"
        :model-value="onboardingData.helpOption"
        :role="onboardingData.role"
        @next="handleStep6Next"
        @back="handleBack"
        @goto-eddy="handleStep6GoToEddy"
        @update:model-value="updateHelpOption"
        @select-section="handleSelectSection"
      />
    </OnboardingLayout>

    <ActivityCatalog
      v-if="currentSection === 1"
      @back="handleSectionBack"
      @start-browsing="handleStartBrowsing"
    />
    <RecommendedActivity
      v-if="currentSection === 2"
      @back="handleSectionBack"
      @view-activity="handleViewActivity"
      @refresh="handleRefresh"
    />
    <CreateLessonForm
      v-if="currentSection === 3"
      @back="handleSectionBack"
      @generate="handleGenerateLesson"
    />
    <TrainerAcademy
      v-if="currentSection === 4"
      @back="handleSectionBack"
      @go-to-academy="handleGoToAcademy"
    />
  </div>
</template>

<script setup>
import OnboardingLayout from "@/components/onboarding/OnboardingLayout.vue";
import RoleSelection from "@/components/onboarding/RoleSelection.vue";
import SchoolSelection from "@/components/onboarding/SchoolSelection.vue";
import GradeSelection from "@/components/onboarding/GradeSelection.vue";
import SubjectSelection from "@/components/onboarding/SubjectSelection.vue";
import DiscoverySelection from "@/components/onboarding/DiscoverySelection.vue";
import HelpSelection from "@/components/onboarding/HelpSelection.vue";
import ActivityCatalog from "@/components/onboarding/sections/ActivityCatalog.vue";
import RecommendedActivity from "@/components/onboarding/sections/RecommendedActivity.vue";
import CreateLessonForm from "@/components/onboarding/sections/CreateLessonForm.vue";
import TrainerAcademy from "@/components/onboarding/sections/TrainerAcademy.vue";
import { ref, computed, onMounted } from "vue";
import userWalkthroughService from "@/services/userWalkthroughService";
import categoryAdminService from "@/services/categoryAdminService";
import categoryService from "@/services/categoryService";
import { useLocaleStore } from "@/store/locale";

const locale = useLocaleStore();

const currentStep = ref(1);
const currentSection = ref(null);
const isSchoolSkipped = ref(false);
const onboardingData = ref({
  role: null,
  school: null,
  subjects: null,
  grades: null,
  discoverySource: null,
  helpOption: null,
});

// Helper function to normalize role for case-insensitive comparison
const normalizeRole = (role) => {
  if (!role) return null;
  return String(role).toLowerCase().trim();
};

const isTrainer = computed(
  () => normalizeRole(onboardingData.value.role) === "trainer"
);

const totalSteps = computed(() => {
  if (normalizeRole(onboardingData.value.role) === "trainer") {
    return 3;
  }
  return 6;
});

const displayStep = computed(() => {
  return currentStep.value;
});

const stepMapping = computed(() => {
  if (isTrainer.value) {
    return {
      role: 1,
      discovery: 2,
      help: 3,
    };
  }
  return {
    role: 1,
    school: 2,
    subject: 3,
    grade: 4,
    discovery: 5,
    help: 6,
  };
});

const shouldShowStep = (stepName) => {
  const mapping = stepMapping.value;
  return currentStep.value === mapping[stepName];
};

const gradeOptions = ref([]);
const subjectOptions = ref([]);

// Discovery source options mapping
const discoveryOptions = [
  { id: 1, label: "Social Media" },
  { id: 2, label: "Search Engine" },
  { id: 3, label: "Friend or Colleague" },
  { id: 4, label: "Email Newsletter" },
  { id: 5, label: "Blog or Article" },
  { id: 6, label: "Conference or Event" },
  { id: 7, label: "Other" },
];

// Help option to assistance prompt mapping
const helpOptionPrompts = {
  1: "Browse hands-on Makercart activities",
  2: "View a Makercart activity in detail",
  3: "Create tech-infused lesson packages",
  4: "Go to Edm8ker Trainer Academy",
};

// Loading state
const isLoading = ref(false);

const handleStep1Next = async (data) => {
  onboardingData.value.role = data.role;
  isSchoolSkipped.value = false;

  // Save role to database
  try {
    isLoading.value = true;
    await userWalkthroughService.chooseRole({ role: data.role });
  } catch (error) {
    console.error("Error saving role:", error);
    // Continue with navigation even if save fails
  } finally {
    isLoading.value = false;
  }

  currentStep.value = 2;
};

const handleStep2Next = async (data) => {
  onboardingData.value.school = data.school;
  isSchoolSkipped.value = false;

  // Reload subject and grade options with locale filtering
  await Promise.all([loadSubjectOptions(), loadGradeOptions()]);

  // Save walkthrough data
  try {
    isLoading.value = true;
    await saveWalkthroughData();
  } catch (error) {
    console.error("Error saving walkthrough:", error);
  } finally {
    isLoading.value = false;
  }

  currentStep.value = stepMapping.value.subject;
};

const handleStep2Skip = async () => {
  onboardingData.value.school = null;
  onboardingData.value.subjects = null;
  onboardingData.value.grades = null;
  isSchoolSkipped.value = true;

  // Save walkthrough data
  try {
    isLoading.value = true;
    await saveWalkthroughData();
  } catch (error) {
    console.error("Error saving walkthrough:", error);
  } finally {
    isLoading.value = false;
  }

  currentStep.value = stepMapping.value.discovery;
};

const handleStep3Next = async (data) => {
  onboardingData.value.subjects = data.selections;

  // Save walkthrough data
  try {
    isLoading.value = true;
    await saveWalkthroughData();
  } catch (error) {
    console.error("Error saving walkthrough:", error);
  } finally {
    isLoading.value = false;
  }

  currentStep.value = stepMapping.value.grade;
};

const handleStep3Skip = async () => {
  onboardingData.value.subjects = null;

  // Save walkthrough data
  try {
    isLoading.value = true;
    await saveWalkthroughData();
  } catch (error) {
    console.error("Error saving walkthrough:", error);
  } finally {
    isLoading.value = false;
  }

  currentStep.value = stepMapping.value.grade;
};

const handleStep4Next = async (data) => {
  onboardingData.value.grades = data.selections;

  // Save walkthrough data
  try {
    isLoading.value = true;
    await saveWalkthroughData();
  } catch (error) {
    console.error("Error saving walkthrough:", error);
  } finally {
    isLoading.value = false;
  }

  currentStep.value = stepMapping.value.discovery;
};

const handleStep4Skip = async () => {
  onboardingData.value.grades = null;

  // Save walkthrough data
  try {
    isLoading.value = true;
    await saveWalkthroughData();
  } catch (error) {
    console.error("Error saving walkthrough:", error);
  } finally {
    isLoading.value = false;
  }

  currentStep.value = stepMapping.value.discovery;
};

const handleStep5Next = async (data) => {
  onboardingData.value.discoverySource = data.discoverySource;

  // Save walkthrough data
  try {
    isLoading.value = true;
    await saveWalkthroughData();
  } catch (error) {
    console.error("Error saving walkthrough:", error);
  } finally {
    isLoading.value = false;
  }

  currentStep.value = stepMapping.value.help;
};

const handleStep5Skip = async () => {
  onboardingData.value.discoverySource = null;

  // Save walkthrough data
  try {
    isLoading.value = true;
    await saveWalkthroughData();
  } catch (error) {
    console.error("Error saving walkthrough:", error);
  } finally {
    isLoading.value = false;
  }

  currentStep.value = stepMapping.value.help;
};

const handleStep6Next = async (data) => {
  onboardingData.value.helpOption = data.selectedOption;

  // Save complete walkthrough data to database
  try {
    isLoading.value = true;
    await saveWalkthroughData();
  } catch (error) {
    console.error("Error saving walkthrough:", error);
    // Continue with navigation even if save fails
  } finally {
    isLoading.value = false;
  }

  // Section navigation is handled by the select-section event from HelpSelection
};

const handleStep6GoToEddy = () => {
  console.log("Go to Eddy clicked");
};

const handleSelectSection = (sectionId) => {
  currentSection.value = sectionId;
};

const handleSectionBack = () => {
  currentSection.value = null;
};

const handleStartBrowsing = () => {
  console.log("Start browsing clicked");
};

const handleViewActivity = () => {
  console.log("View activity clicked");
};

const handleRefresh = () => {
  console.log("Refresh clicked");
};

const handleGenerateLesson = (data) => {
  console.log("Generate lesson:", data);
};

const handleGoToAcademy = () => {
  console.log("Go to Trainer Academy clicked");
};

const handleBack = () => {
  if (currentStep.value > 1) {
    // When school was skipped, back from discovery (5) jumps directly to school (2)
    if (isSchoolSkipped.value && currentStep.value === 5 && !isTrainer.value) {
      currentStep.value = 2;
      isSchoolSkipped.value = false;
    } else {
      currentStep.value--;
      if (currentStep.value === 2 && !isTrainer.value) {
        isSchoolSkipped.value = false;
      }
    }
  }
};

// Update handlers to prevent recursive updates
const updateRole = (value) => {
  onboardingData.value.role = value;
};

const updateSchool = (value) => {
  onboardingData.value.school = value;
};

const updateGrades = (value) => {
  onboardingData.value.grades = value;
};

const updateSubjects = (value) => {
  onboardingData.value.subjects = value;
};

const updateDiscoverySource = (value) => {
  onboardingData.value.discoverySource = value;
};

const updateHelpOption = (value) => {
  onboardingData.value.helpOption = value;
};

// Helper function to map subject IDs to labels
const mapSubjectIdsToLabels = (subjectIds) => {
  if (!subjectIds || subjectIds.length === 0) return [];
  return subjectIds
    .map((id) => {
      const subject = subjectOptions.value.find((s) => s.id === id);
      return subject ? subject.label : null;
    })
    .filter(Boolean);
};

// Helper function to map grade IDs to labels
const mapGradeIdsToLabels = (gradeIds) => {
  if (!gradeIds || gradeIds.length === 0) return [];
  return gradeIds
    .map((id) => {
      const grade = gradeOptions.value.find((g) => g.id === id);
      return grade ? grade.label : null;
    })
    .filter(Boolean);
};

// Helper function to get discovery source label
const getDiscoverySourceLabel = (discoverySource) => {
  if (!discoverySource) return null;
  if (discoverySource.otherText) {
    return discoverySource.otherText;
  }
  const option = discoveryOptions.find(
    (opt) => opt.id === discoverySource.sourceId
  );
  return option ? option.label : null;
};

// Helper function to map help option to assistance prompts
const mapHelpOptionToPrompts = (helpOptionId) => {
  const prompts = {
    assitance_prompt_1: false,
    assitance_prompt_2: false,
    assitance_prompt_3: false,
    assitance_prompt_4: false,
  };

  if (helpOptionId && helpOptionPrompts[helpOptionId]) {
    // Map the selected option to the corresponding prompt
    if (helpOptionId === 1) {
      prompts.assitance_prompt_1 = true;
    } else if (helpOptionId === 2) {
      prompts.assitance_prompt_2 = true;
    } else if (helpOptionId === 3) {
      prompts.assitance_prompt_3 = true;
    } else if (helpOptionId === 4) {
      prompts.assitance_prompt_4 = true;
    }
  }

  return prompts;
};

// Function to save walkthrough data
const saveWalkthroughData = async () => {
  const data = onboardingData.value;

  // Map the data to API format
  const walkthroughPayload = {
    role: data.role || null,
    school: data.school?.name || null,
    school_country: data.school?.location || null,
    subjects: mapSubjectIdsToLabels(data.subjects),
    grades: mapGradeIdsToLabels(data.grades),
    discovery_source: getDiscoverySourceLabel(data.discoverySource),
    location: locale.currentLocale,
    ...mapHelpOptionToPrompts(data.helpOption),
  };

  await userWalkthroughService.recordWalkthrough(walkthroughPayload);
};

// Function to determine current step based on filled data
const determineCurrentStep = () => {
  const data = onboardingData.value;
  const mapping = stepMapping.value;

  // If no role selected, start at step 1
  if (!data.role) {
    return 1;
  }

  // For trainer flow
  if (normalizeRole(data.role) === "trainer") {
    if (!data.discoverySource) {
      return mapping.discovery;
    }
    if (!data.helpOption) {
      return mapping.help;
    }
    // All steps completed
    return mapping.help;
  }

  // For teacher flow
  if (data.helpOption) return mapping.help;
  if (data.discoverySource) return mapping.help;

  if (isSchoolSkipped.value) {
    return mapping.discovery;
  }

  if (data.grades && data.grades.length > 0) return mapping.discovery;
  if (data.subjects && data.subjects.length > 0) return mapping.grade;
  if (data.school) return mapping.subject;

  return mapping.school;
};

// Load existing walkthrough data on mount
const loadWalkthroughData = async () => {
  try {
    isLoading.value = true;
    const response = await userWalkthroughService.getMyWalkthrough();

    if (response?.data?.data) {
      const walkthroughData = response.data.data;

      // Map API data back to component format
      if (walkthroughData.role) {
        onboardingData.value.role = walkthroughData.role;
      }

      if (walkthroughData.school) {
        onboardingData.value.school = {
          name: walkthroughData.school,
          location: walkthroughData.school_country || null,
        };
        // Reload subject and grade options with locale filtering after school is set
        await Promise.all([loadSubjectOptions(), loadGradeOptions()]);
      }

      // Map subject labels back to IDs
      if (walkthroughData.subjects && walkthroughData.subjects.length > 0) {
        onboardingData.value.subjects = walkthroughData.subjects
          .map((label) => {
            const subject = subjectOptions.value.find((s) => s.label === label);
            return subject ? subject.id : null;
          })
          .filter(Boolean);
      }

      // Map level labels back to IDs
      if (walkthroughData.grades && walkthroughData.grades.length > 0) {
        onboardingData.value.grades = walkthroughData.grades
          .map((label) => {
            const grade = gradeOptions.value.find((g) => g.label === label);
            return grade ? grade.id : null;
          })
          .filter(Boolean);
      }

      // Map discovery source back
      if (walkthroughData.discovery_source) {
        const discoveryOption = discoveryOptions.find(
          (opt) => opt.label === walkthroughData.discovery_source
        );
        if (discoveryOption) {
          onboardingData.value.discoverySource = {
            sourceId: discoveryOption.id,
            otherText:
              discoveryOption.id === 7
                ? walkthroughData.discovery_source
                : null,
          };
        } else {
          // Custom "Other" text
          onboardingData.value.discoverySource = {
            sourceId: 7,
            otherText: walkthroughData.discovery_source,
          };
        }
      }

      // Map assistance prompts back to help option
      if (walkthroughData.assitance_prompt_1) {
        onboardingData.value.helpOption = 1;
      } else if (walkthroughData.assitance_prompt_2) {
        onboardingData.value.helpOption = 2;
      } else if (walkthroughData.assitance_prompt_3) {
        onboardingData.value.helpOption = 3;
      } else if (walkthroughData.assitance_prompt_4) {
        onboardingData.value.helpOption = 4;
      }

      if (!onboardingData.value.school) {
        if (
          onboardingData.value.discoverySource ||
          onboardingData.value.helpOption
        ) {
          isSchoolSkipped.value = true;
        } else if (
          onboardingData.value.subjects?.length > 0 ||
          onboardingData.value.grades?.length > 0
        ) {
          isSchoolSkipped.value = true;
        }
      }

      // Set current step based on filled data
      currentStep.value = determineCurrentStep();
    }
  } catch (error) {
    console.error("Error loading walkthrough data:", error);
    // Continue with empty form if load fails
  } finally {
    isLoading.value = false;
  }
};
const getSchoolLocale = () => {
  const raw = onboardingData.value.school?.location;
  if (!raw) return undefined;
  const normalized = String(raw).toUpperCase().trim();
  if (normalized === "SG" || normalized === "SINGAPORE") return "sg";
  return "us";
};

const loadTermOptions = async (search) => {
  const categoryResponse = await categoryAdminService.getAll({ search });
  const category = categoryResponse?.data?.data?.rows?.[0];
  if (!category) return [];

  const termsResponse = await categoryService.getTermsDropdown({
    taxonomy: category.id,
    locale: getSchoolLocale(),
    sort_by: "priority",
    order: "asc",
  });
  const terms = termsResponse?.data?.data?.rows || [];

  terms.sort((a, b) => {
    const priorityA = a.priority ?? Infinity;
    const priorityB = b.priority ?? Infinity;
    return priorityA - priorityB;
  });

  return terms.map((term) => ({
    id: term.id,
    label: term.title,
  }));
};

const loadSubjectOptions = async () => {
  try {
    subjectOptions.value = await loadTermOptions("subject");
  } catch (error) {
    console.error("Error loading subject options:", error);
  }
};

const loadGradeOptions = async () => {
  try {
    gradeOptions.value = await loadTermOptions("grade");
  } catch (error) {
    console.error("Error loading grade options:", error);
  }
};

onMounted(async () => {
  await Promise.all([loadSubjectOptions(), loadGradeOptions()]);
  loadWalkthroughData();
});
</script>

<style scoped>
.school-selection-wrapper {
  padding-bottom: 250px;
  width: 100%;
}

@media (max-width: 768px) {
  .school-selection-wrapper {
    justify-items: center;
  }
}
</style>
